import { PaymentMethodsResponse } from '@adyen/adyen-web/dist/types/core/ProcessResponse/PaymentMethodsResponse/types';
import { useCallback, useEffect, useState } from 'react';

import RetryNotification from '../RetryNotification/RetryNotification';
import RetryPaymentLoader from '../RetryPaymentLoader/RetryPaymentLoader';

import AdyenApplePayButton from '@features/ApplePay/AdyenApplePayButton/AdyenApplePayButton';
import { useTranslation } from '@hooks/useTranslations';
import { useAppContext } from '@providers/AppContextProvider';
import { useConfig } from '@providers/ConfigProvider';
import { useRetryPaymentContext } from '@providers/RetryPaymentContextProvider';
import { initiateApplePayPayment } from '@services/paymentService';
import { getLocalPaymentMethods } from '@services/retryPaymentService';
import { captureException } from '@services/sentryService';
import { encodePageUrlasBase64 } from '@utils/retryUtils';
import { convertMajorUnitsToMinorUnits, convertRunningEnvToAdyenEnv } from '@utils/utils';

import styles from './RetryPaymentApplePay.module.scss';

export type RetryPaymentApplePayProps = {
  orderId: string;
  siteId: number;
  currency: string;
  customerId: string;
  amount: number;
  sid?: string;
};

const RetryPaymentApplePay: React.FC<RetryPaymentApplePayProps> = ({
  orderId,
  siteId,
  currency,
  customerId,
  amount,
  sid,
}) => {
  const { csrfToken } = useAppContext();
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [countryCode, setCountryCode] = useState('');
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodsResponse>({
    paymentMethods: [],
  });
  const { setRetryPaymentInfo } = useRetryPaymentContext();
  const {
    config: {
      env,
      adyen: { clientKey },
    },
  } = useConfig();
  const { t } = useTranslation();

  useEffect(() => {
    // Request to fetch paymentMethodsResponse & countryCode
    const fetchPaymentMethods = async () => {
      try {
        setLoading(true);
        const response = await getLocalPaymentMethods(siteId, customerId, amount);

        setCountryCode(response.countryCode);
        // Adapt the JSON response from retry-payment to the object that is accepted by Adyen
        const paymentMethodsResponse: PaymentMethodsResponse = {
          paymentMethods: response.paymentMethods,
        };
        setPaymentMethods(paymentMethodsResponse);
      } catch (err: any) {
        console.log('Error while getting local payment methods:', err);
        captureException(err);
        setHasError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentMethods();
  }, []);

  const onSubmit = useCallback(async (applePayToken: string) => {
    setLoading(true);
    try {
      /* The "returnUrl" for Apple Pay retry is actually not in used, but the backend requires this value. So we once
       * used the "NoUseForApplePay", but then for security reason, we changed it to use the actual URL here. */
      const returnUrl = encodePageUrlasBase64();
      const response = await initiateApplePayPayment({
        csrfToken: csrfToken as string,
        orderId,
        customerId,
        tokenId: applePayToken,
        returnUrl,
      });
      const { retryPaymentStatus } = response;
      const retryPossible =
        retryPaymentStatus === 'CANCELLED' || retryPaymentStatus === 'UNSUCCESSFUL';
      setRetryPaymentInfo({ retryPossible, retryPaymentStatus });
    } catch (e: any) {
      captureException(e);
      setRetryPaymentInfo({ retryPossible: false, retryPaymentStatus: 'UNKNOWN' });
    } finally {
      setLoading(false);
    }
  }, []);

  const onFatalError = useCallback(
    (error: unknown) => {
      captureException({ error, message: 'Error received from Apple Pay button' });
      setRetryPaymentInfo({ retryPossible: false, retryPaymentStatus: 'CANCELLED' });
    },
    [setRetryPaymentInfo],
  );

  /* Convert the major units amount to minor units to pass to <AdyenApplePayButton /> below.
   * The <AdyenApplePayButton /> expect the amount in minor units.  */
  const minorUnitsAmount = convertMajorUnitsToMinorUnits(amount);

  if (!loading && hasError) {
    return (
      <RetryNotification
        id="retryPaymentApplePayError"
        sid={sid}
        currency={currency}
        siteId={siteId}
        customerId={customerId}
        ariaLabel={t('general.error.internal')}
        type="warning">
        {t('general.error.internal')}
      </RetryNotification>
    );
  }

  return (
    <div className={styles.retryApplePayPayment}>
      {loading && <RetryPaymentLoader />}

      <RetryNotification
        id="retryPaymentNotification"
        sid={sid}
        currency={currency}
        siteId={siteId}
        customerId={customerId}
        ariaLabel={t('mzp.order.detail.retry.payment.accessibility.label')}
        type="warning">
        <div className={styles.retryApplePayPaymentNotification}>
          {t('mzp.order.detail.payment.reminder.warning.applepay')}
          <div
            className={styles.retryApplePayPaymentButtonContainer}
            data-zta="retryApplePayPaymentContainer">
            <AdyenApplePayButton
              adyenEnvironment={convertRunningEnvToAdyenEnv(env)}
              clientKey={clientKey}
              countryCode={countryCode}
              currency={currency}
              amount={minorUnitsAmount}
              paymentMethodsResponse={paymentMethods}
              onSubmit={onSubmit}
              onFatalError={onFatalError}
              buttonColor="white-with-line"
            />
          </div>
        </div>
      </RetryNotification>
    </div>
  );
};

export default RetryPaymentApplePay;
